import { authApi, readStoredToken, readStoredUser } from './authApi';

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').trim();
const AD_PUSH_BASE_URL = `${API_BASE_URL}/ad-push`;

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL must be configured for the frontend build');
}

let lastConnectionError = '';

function getMainToken() {
  return readStoredToken();
}

function getMainUser() {
  return readStoredUser();
}

function isConfigured() {
  return Boolean(API_BASE_URL);
}

function getConfigurationError() {
  return '';
}

function getLastConnectionError() {
  return lastConnectionError;
}

function getStreamToken() {
  return getMainToken();
}

function getStreamUser() {
  return getMainUser();
}

function clearStreamAuth() {
  lastConnectionError = '';
}

async function autoConnect() {
  lastConnectionError = '';

  const token = getMainToken();
  const user = getMainUser();

  if (!token || !user?.id) {
    lastConnectionError = 'Main Polar Ace session is missing.';
    return false;
  }

  return true;
}

function authHeaders(includeJson = false) {
  const token = getMainToken();
  const headers = {};

  if (includeJson) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return {};
  }
}

function normalizeCollection(data) {
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.items)) {
    return data.items;
  }
  if (Array.isArray(data.media)) {
    return data.media;
  }
  if (Array.isArray(data.submissions)) {
    return data.submissions;
  }
  return [];
}

async function streamFetch(path, options = {}) {
  let res;
  try {
    res = await fetch(`${AD_PUSH_BASE_URL}${path}`, {
      ...options,
      headers: { ...authHeaders(), ...options.headers },
    });
  } catch {
    throw new Error('Could not reach the merged ad-push service.');
  }

  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.error || data.detail || `Request failed (${res.status})`);
  }

  return data;
}

async function apiFetch(path, options = {}) {
  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers: { ...authHeaders(), ...options.headers },
    });
  } catch {
    throw new Error('Could not reach the Polar Ace API.');
  }

  const data = await parseJson(res);
  if (!res.ok) {
    throw new Error(data.error || data.detail || `Request failed (${res.status})`);
  }

  return data;
}

function isAdminAccessError(error) {
  const message = error?.message || '';
  return (
    message === 'Admin access required'
    || message === 'Admin access is not configured'
  );
}

async function getMyBalance() {
  const mainUser = getMainUser();
  if (!mainUser?.id) {
    return 0;
  }

  try {
    const data = await authApi.getPaymentBalance(mainUser.id, getMainToken());
    return data.spade_balance ?? 0;
  } catch {
    return 0;
  }
}

async function updateSettings(settings) {
  return streamFetch('/settings/me', {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify(settings),
  });
}

async function getLiveStatus() {
  return streamFetch('/live/me');
}

async function startLiveStream(payload = {}) {
  return streamFetch('/live/start', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  });
}

async function stopLiveStream() {
  return streamFetch('/live/stop', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({}),
  });
}

async function heartbeatLiveStream() {
  return streamFetch('/live/heartbeat', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({}),
  });
}

async function sendWatchHeartbeat(streamerId) {
  const normalized = String(streamerId || '').trim();
  if (!normalized) return null;
  return streamFetch('/live/watch', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ streamer_id: normalized }),
  });
}

async function getLiveStatuses(userIds = []) {
  const normalizedUserIds = Array.from(
    new Set(
      (Array.isArray(userIds) ? userIds : [])
        .map((userId) => String(userId || '').trim())
        .filter(Boolean)
    )
  );

  if (!normalizedUserIds.length) {
    return [];
  }

  const data = await streamFetch('/live/statuses', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ user_ids: normalizedUserIds }),
  });

  return Array.isArray(data.statuses) ? data.statuses : [];
}

async function getMediaLibrary() {
  const data = await streamFetch('/media');
  return normalizeCollection(data);
}

async function getPublicUserMedia(userId) {
  if (!userId) return [];
  const data = await streamFetch(`/media/public/${encodeURIComponent(userId)}`);
  return normalizeCollection(data);
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(new Error('Failed to read media file'));
    reader.readAsDataURL(file);
  });
}

async function uploadMedia(title, file) {
  const trimmedTitle = typeof title === 'string' ? title.trim() : '';
  if (!trimmedTitle) {
    throw new Error('Title is required');
  }
  if (!file) {
    throw new Error('Image file is required');
  }

  const uploadData = await streamFetch('/media/upload-url', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
    }),
  });

  await authApi.uploadFileToPresignedUrl(
    uploadData.uploadUrl,
    file,
    file.type || 'application/octet-stream',
  );

  return streamFetch('/media', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({
      title: trimmedTitle,
      media_id: uploadData.media_id,
      upload_key: uploadData.key,
    }),
  });
}

async function deleteMedia(mediaId) {
  if (!mediaId) {
    throw new Error('Media id is required');
  }

  return streamFetch(`/media/${encodeURIComponent(mediaId)}`, {
    method: 'DELETE',
  });
}

async function getSubmissions(status) {
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const data = await streamFetch(`/submissions${query}`);
  return normalizeCollection(data);
}

async function getFanSubmissions() {
  const data = await streamFetch('/submissions?view=fan');
  return normalizeCollection(data);
}

async function reviewSubmission(submissionId, newStatus) {
  return streamFetch(`/submissions/${encodeURIComponent(submissionId)}`, {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify({ status: newStatus }),
  });
}

async function toggleShowOnStream(submissionId) {
  return streamFetch(`/submissions/${encodeURIComponent(submissionId)}`, {
    method: 'PATCH',
  });
}

async function updateSubmissionSchedule(submissionId, schedule) {
  return streamFetch(`/submissions/${encodeURIComponent(submissionId)}`, {
    method: 'PATCH',
    headers: authHeaders(true),
    body: JSON.stringify({ schedule }),
  });
}

async function updateSubmissionPlayback(submissionId, payload) {
  if (!submissionId) {
    throw new Error('Submission id is required');
  }

  return streamFetch(`/submissions/${encodeURIComponent(submissionId)}`, {
    method: 'PATCH',
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  });
}

async function createSubmission(payload) {
  return streamFetch('/submissions', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(payload),
  });
}

async function getAdsForOverlay(userId) {
  const mainUser = getMainUser();
  const resolvedUserId = userId || mainUser?.id;
  if (!resolvedUserId) {
    return { images: [] };
  }

  return streamFetch('/images/rotate', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ user_id: resolvedUserId }),
  });
}

function resolveImageUrl(urlOrKey) {
  if (!urlOrKey) {
    return null;
  }
  if (urlOrKey.startsWith('http://') || urlOrKey.startsWith('https://')) {
    return urlOrKey;
  }
  return `${AD_PUSH_BASE_URL}/media/${urlOrKey}`;
}

function getViewerCamOverlayUrl() {
  const token = getMainToken();
  const user = getMainUser();
  if (!token || !user?.id) return null;

  const params = new URLSearchParams({
    api_url: API_BASE_URL,
    token,
    user_id: user.id,
  });

  const origin = window.location.origin;
  return `${origin}/overlay/viewer-cams?${params.toString()}`;
}

function getOverlayUrl(settings = {}) {
  const token = getMainToken();
  const user = getMainUser();
  if (!token || !user?.id) {
    return null;
  }

  const params = new URLSearchParams({
    api_url: API_BASE_URL,
    token,
    user_id: user.id,
    position: settings.position || 'bottom-right',
    size: settings.size || 'medium',
    theme: settings.theme || 'glass',
    margin: String(settings.margin || 24),
  });

  return `${AD_PUSH_BASE_URL}/overlay?${params.toString()}`;
}

async function getAdminPendingMedia() {
  const data = await streamFetch('/admin/media');
  return normalizeCollection(data);
}

async function getAdminUserMedia(userId) {
  if (!userId) {
    throw new Error('User id is required');
  }

  const data = await streamFetch(`/admin/users/${encodeURIComponent(userId)}/media`);
  return normalizeCollection(data);
}

async function canAccessAdmin() {
  try {
    await streamFetch('/admin/media');
    return true;
  } catch (error) {
    if (isAdminAccessError(error)) {
      return false;
    }
    throw error;
  }
}

async function getLiveStreams() {
  const data = await apiFetch('/streams/live');
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.streams)) return data.streams;
  return [];
}

async function getLiveStreamByLogin(login) {
  const normalized = String(login || '').trim();
  if (!normalized) {
    throw new Error('Streamer login is required');
  }
  return apiFetch(`/streams/${encodeURIComponent(normalized)}`);
}

async function getChatToken(streamerId, username) {
  const normalized = String(streamerId || '').trim();
  if (!normalized) {
    throw new Error('streamerId is required');
  }
  const body = { streamerId: normalized };
  const trimmedUsername = String(username || '').trim();
  if (trimmedUsername) body.username = trimmedUsername;
  return streamFetch('/chat/token', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
}

async function subscribeToStreamer(streamerUserId) {
  const normalized = String(streamerUserId || '').trim();
  if (!normalized) {
    throw new Error('streamerUserId is required');
  }
  return streamFetch('/subscriptions', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ streamer_user_id: normalized }),
  });
}

async function getMySubscriptions() {
  const data = await streamFetch('/subscriptions');
  return Array.isArray(data) ? data : [];
}

async function checkSubscription(streamerUserId) {
  const normalized = String(streamerUserId || '').trim();
  if (!normalized) {
    return { subscribed: false, subscription: null };
  }
  return streamFetch(`/subscriptions/check/${encodeURIComponent(normalized)}`);
}

async function cancelSubscriptionRequest(streamerUserId) {
  const normalized = String(streamerUserId || '').trim();
  if (!normalized) {
    throw new Error('streamerUserId is required');
  }
  return streamFetch(`/subscriptions/${encodeURIComponent(normalized)}`, {
    method: 'DELETE',
  });
}

async function getMySubscribers() {
  const data = await streamFetch('/subscribers');
  return Array.isArray(data) ? data : [];
}

async function getFeaturedCreators(limit = 5) {
  const normalizedLimit = Math.max(1, Math.min(Number(limit) || 5, 20));
  const data = await apiFetch(`/users/featured?limit=${normalizedLimit}`);
  return Array.isArray(data) ? data : [];
}

// ── IVS Real-Time Stage token ─────────────────────────────────────────────

async function getIvsToken(room, identity, role = 'subscriber') {
  return streamFetch(
    `/video-request/ivs-token?room=${encodeURIComponent(room)}&identity=${encodeURIComponent(identity)}&role=${encodeURIComponent(role)}`,
    { method: 'POST' },
  );
}

// ── Video Request API ─────────────────────────────────────────────────────

async function sendVideoRequest(streamerId, durationSeconds = 30) {
  return streamFetch('/video-request', {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ streamer_id: streamerId, duration_seconds: durationSeconds }),
  });
}

async function getMyVideoRequestStatus(streamerId) {
  return streamFetch(`/video-request/my-status?streamer_id=${encodeURIComponent(streamerId)}`);
}

async function getVideoRequestConfig(streamerId) {
  return streamFetch(`/video-request/config/${encodeURIComponent(streamerId)}`);
}

async function getVideoRequestsForStreamer() {
  return streamFetch('/video-request/for-streamer');
}

async function approveVideoRequest(requestId) {
  return streamFetch(`/video-request/${encodeURIComponent(requestId)}/approve`, {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify({}),
  });
}

async function rejectVideoRequest(requestId) {
  return streamFetch(`/video-request/${encodeURIComponent(requestId)}/reject`, {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify({}),
  });
}

async function endVideoRequest(requestId) {
  return streamFetch(`/video-request/${encodeURIComponent(requestId)}/end`, {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify({}),
  });
}

async function extendVideoRequest(requestId, extraSeconds = 30) {
  return streamFetch(`/video-request/${encodeURIComponent(requestId)}/extend`, {
    method: 'PUT',
    headers: authHeaders(true),
    body: JSON.stringify({ extra_seconds: extraSeconds }),
  });
}

async function getStreamerStageToken() {
  return streamFetch('/ivs-token/streamer', { headers: authHeaders() });
}

function buildSignalWsUrl(streamerId, role, participantId) {
  const localAdPush = (process.env.REACT_APP_LOCAL_AD_PUSH_URL || '').trim();
  const wsBase = localAdPush
    ? localAdPush.replace(/^http/, 'ws')
    : AD_PUSH_BASE_URL.replace(/^http/, 'ws');
  const token = getMainToken();
  const params = new URLSearchParams({ role, viewer_id: participantId });
  if (token) params.set('token', token);
  return `${wsBase}/video-request/signal/${encodeURIComponent(streamerId)}?${params.toString()}`;
}

async function approveMedia(mediaId) {
  return streamFetch(`/admin/media/${encodeURIComponent(mediaId)}/approve`, {
    method: 'PUT',
  });
}

async function rejectMedia(mediaId) {
  return streamFetch(`/admin/media/${encodeURIComponent(mediaId)}/reject`, {
    method: 'PUT',
  });
}

const streamApi = {
  autoConnect,
  getMyBalance,
  getStreamToken,
  getStreamUser,
  clearStreamAuth,
  updateSettings,
  getLiveStatus,
  startLiveStream,
  stopLiveStream,
  heartbeatLiveStream,
  sendWatchHeartbeat,
  getLiveStatuses,
  getMediaLibrary,
  getPublicUserMedia,
  uploadMedia,
  deleteMedia,
  getSubmissions,
  getFanSubmissions,
  createSubmission,
  reviewSubmission,
  toggleShowOnStream,
  updateSubmissionSchedule,
  updateSubmissionPlayback,
  getAdsForOverlay,
  resolveImageUrl,
  getOverlayUrl,
  getViewerCamOverlayUrl,
  getAdminPendingMedia,
  getAdminUserMedia,
  canAccessAdmin,
  isAdminAccessError,
  approveMedia,
  rejectMedia,
  subscribeToStreamer,
  getMySubscriptions,
  checkSubscription,
  cancelSubscription: cancelSubscriptionRequest,
  getMySubscribers,
  getFeaturedCreators,
  getLiveStreams,
  getLiveStreamByLogin,
  getChatToken,
  isConfigured,
  getConfigurationError,
  STREAM_API_URL: AD_PUSH_BASE_URL,
  getLastConnectionError,
  getIvsToken,
  sendVideoRequest,
  getMyVideoRequestStatus,
  getVideoRequestConfig,
  getVideoRequestsForStreamer,
  approveVideoRequest,
  rejectVideoRequest,
  endVideoRequest,
  extendVideoRequest,
  getStreamerStageToken,
  buildSignalWsUrl,
};

export default streamApi;
